import { OktaLoginTypeResponse } from 'app/api/apiClient/generated/types';
import { keys } from 'app/config/keys';
import { getLocalStorageTheme, getPrefersDarkMode } from 'app/hooks/useUiTheme';
import { GeneralDispatch } from 'app/state/GeneralContext/contexts';

import { BeamerConfig, BeamerUserDto } from './types';

export enum OktaBeamerSegments {
  OktaEnabled = '_oktaEnabled_',
  OktaNotEnabled = '_oktaNotEnabled_',
}

const getOktaSegments = (oktaInfo: null | OktaLoginTypeResponse): OktaBeamerSegments[] => {
  if (oktaInfo?.clientId) {
    return [OktaBeamerSegments.OktaEnabled];
  }

  if (oktaInfo) {
    return [OktaBeamerSegments.OktaNotEnabled];
  }

  return [];
};

export const getSegmentsFromUser = (beamerUserDto: BeamerUserDto): string[] => [
  ...getOktaSegments(beamerUserDto.oktaInfo),
  ...beamerUserDto.beamerSegments,
];

const isBeamerDefined = () => typeof Beamer !== 'undefined';

export const showBeamer = (dispatch: GeneralDispatch) => {
  if (isBeamerDefined()) {
    Beamer.show();

    dispatch({
      payload: { beamerMessageCount: 0 },
      type: 'setBeamerMessageCount',
    });
  }
};

const addBeamerConfigScript = () => {
  const head = document.querySelector('head');
  const script = document.createElement('script');
  const localStorageTheme = getLocalStorageTheme();
  const prefersDarkMode = getPrefersDarkMode();

  const theme = localStorageTheme === 'system' ? (prefersDarkMode ? 'dark' : 'light') : localStorageTheme;

  script.type = 'text/javascript';
  script.innerHTML = `
    var beamer_config = {
      alert: false,
      button_position: 'bottom-right',
      display: 'right',
      language: 'EN',
      lazy: true,
      product_id: '${keys.BEAMER_PROJECT_ID}',
      right: 0,
      top: 0,
      theme: '${theme}',
    };
  `;

  head?.appendChild(script);
};

const addBeamerScript = () => {
  const head = document.querySelector('head');
  const script = document.createElement('script');
  script.src = 'https://app.getbeamer.com/js/beamer-embed.js';
  script.defer = true;
  script.type = 'text/javascript';

  head?.appendChild(script);
};

export const setupBeamer = () => {
  addBeamerConfigScript();
  addBeamerScript();
};

export const initBeamer = (
  callback: BeamerConfig['callback'],
  beamerUserDto: BeamerUserDto,
) => {
  const segments = getSegmentsFromUser(beamerUserDto);

  const config: Partial<BeamerConfig> = {
    callback,
    filter: segments.join(';'),
    /* eslint-disable @typescript-eslint/naming-convention */
    user_email: beamerUserDto.user?.email ?? '',
    user_id: beamerUserDto.user?.id ?? '',
    /* eslint-enable @typescript-eslint/naming-convention */
  };

  if (isBeamerDefined()) {
    Beamer.update(config);
    Beamer.init();
  }
};

export const destroyBeamer = () => {
  if (isBeamerDefined()) {
    Beamer.destroy();
  }
};

export const setBeamerTheme = (theme: 'dark' | 'light') => {
  if (isBeamerDefined()) {
    Beamer.update({ theme });
  }
};
